import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, apiError, fileUrl } from "@/lib/api";
import { PROPERTY_TYPES, formatPrice, AMMAN_CENTER } from "@/lib/constants";
import { typeLabel } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import MapPicker from "@/components/MapPicker";
import LocationPicker from "@/components/LocationPicker";
import BackButton from "@/components/BackButton";
import { Sparkles, X, Star, Upload, ChevronLeft, ChevronRight, Copy, Facebook, MessageCircle, Info } from "lucide-react";
import { toast } from "sonner";

const FIELDS_BY_TYPE = {
  land: ["area_sqm", "zoning", "frontage", "streets", "notes"],
  apartment: ["area_sqm", "bedrooms", "bathrooms", "floor", "age", "notes"],
  villa: ["land_area", "built_area", "bedrooms", "bathrooms", "floors", "notes"],
  building: ["land_area", "built_area", "floors", "units", "income", "notes"],
  commercial: ["activity", "area_sqm", "frontage", "streets", "notes"],
  industrial: ["land_area", "built_area", "usage", "notes"],
  farm: ["land_area", "built_area", "notes"],
  hotel: ["land_area", "built_area", "floors", "units", "notes"],
  other: ["area_sqm", "notes"],
};

export default function AddProperty() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const { id: editId } = useParams();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [publishedId, setPublishedId] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const [d, setD] = useState({
    title: "", type: "", governorate: "", city: "", area: "",
    lat: null, lng: null, price: "", price_negotiable: false,
    description: "", images: [], cover_index: 0,
    show_phone: true, show_whatsapp: true,
    contact_phone: user?.phone || "", contact_whatsapp: user?.phone || "",
    details: {},
  });
  const [agreedTerms, setAgreedTerms] = useState(false);

  useEffect(() => {
    if (editId) {
      api.get(`/properties/${editId}`).then((r) => {
        const p = r.data;
        setD({ ...p, price: String(p.price || ""), details: p.details || {} });
      });
      return;
    }
    // Prefill from AI Valuation → "publish for free" flow
    try {
      const raw = sessionStorage.getItem("fluorit_ai_prefill");
      if (raw) {
        const pre = JSON.parse(raw);
        setD((s) => ({
          ...s,
          type: pre.type || s.type,
          governorate: pre.governorate || s.governorate,
          city: pre.city || s.city,
          area: pre.area || s.area,
          lat: pre.lat ?? s.lat, lng: pre.lng ?? s.lng,
          price: pre.asking_price ? String(pre.asking_price) : s.price,
          details: { ...s.details, ...(pre.details || {}) },
        }));
        sessionStorage.removeItem("fluorit_ai_prefill");
        toast.success("تم تعبئة بيانات عقارك من التقييم — أضف الصور وانشر إعلانك");
      }
    } catch {}
  }, [editId]);

  const totalSteps = 7;
  const progress = Math.round((step / totalSteps) * 100);
  const set = (k, v) => setD((s) => ({ ...s, [k]: v }));
  const setDetail = (k, v) => setD((s) => ({ ...s, details: { ...s.details, [k]: v } }));

  const fields = FIELDS_BY_TYPE[d.type] || FIELDS_BY_TYPE.other;

  const canNext = () => {
    if (step === 1) return !!d.type;
    if (step === 2) return !!d.city;
    if (step === 5) return !!d.price;
    if (step === 6) return !!d.title;
    return true;
  };

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.path;
  };
  const suggestAreaIfCustom = async () => {
    if (!d.city || !d.area) return;
    try { await api.post("/area-suggestions", { name: d.area, city: d.city }); } catch {}
  };
  const onFiles = async (files) => {
    const arr = Array.from(files || []).slice(0, 20);
    for (const f of arr) {
      try { const path = await uploadImage(f); setD((s) => ({ ...s, images: [...s.images, path] })); }
      catch (e) { toast.error(apiError(e)); }
    }
  };

  const aiEnhance = async () => {
    if (!d.description.trim()) { toast.error("اكتب وصفاً أولاً"); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/enhance-description", { text: d.description, type: d.type, language: lang });
      set("description", data.text);
      toast.success(lang === "ar" ? "تم تحسين الوصف ✨" : "Description improved ✨");
    } catch (e) { toast.error(apiError(e)); }
    finally { setAiLoading(false); }
  };

  const submit = async () => {
    if (!agreedTerms) { toast.error("يجب الموافقة على الشروط قبل نشر الإعلان"); return; }
    setSaving(true);
    try {
      const payload = { ...d, price: parseFloat(d.price), terms_accepted: true };
      const res = editId ? await api.put(`/properties/${editId}`, payload) : await api.post("/properties", payload);
      await suggestAreaIfCustom();
      setPublishedId(res.data.id);
      setStep(8);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (step === 8 && publishedId) {
    const url = `${window.location.origin}/property/${publishedId}`;
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 text-center">
        <div className="text-6xl mb-3">🎉</div>
        <h1 className="text-3xl font-extrabold text-emerald-950">{t.wizard.done}</h1>
        <div className="mt-4 inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-900 text-sm rounded-full px-4 py-2">
          <Info className="w-4 h-4" /> {t.wizard.pendingNote}
        </div>
        <p className="mt-4 text-slate-600 text-sm">{t.wizard.listingId}: <span className="font-mono">{publishedId}</span></p>
        <div className="mt-8 flex flex-wrap gap-2 justify-center">
          <Button onClick={() => nav("/dashboard")} className="bg-emerald-900 hover:bg-emerald-950">{t.dash.title}</Button>
          <Button variant="outline" onClick={() => nav("/")}>{t.nav.home}</Button>
        </div>
        <div className="mt-8">
          <div className="text-sm font-semibold text-slate-600 mb-2">{t.wizard.share}</div>
          <div className="flex gap-2 justify-center">
            <a href={`https://wa.me/?text=${encodeURIComponent(url)}`} target="_blank" rel="noreferrer"><Button className="bg-green-600 hover:bg-green-700"><MessageCircle className="w-4 h-4" /></Button></a>
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} target="_blank" rel="noreferrer"><Button variant="outline"><Facebook className="w-4 h-4" /></Button></a>
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(url); toast.success(t.detail.copyLink); }}><Copy className="w-4 h-4" /></Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
      <div className="mb-4"><BackButton to="/dashboard" label={t.back.toDash} /></div>
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-emerald-950">{t.wizard.title}</h1>
        <p className="text-slate-600 mt-1">{t.wizard.subtitle}</p>
        <div className="mt-3 flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-950 rounded-xl px-3 py-2">
          <Info className="w-4 h-4 shrink-0 mt-0.5 text-amber-700" />
          <span>بصفتك صاحب الإعلان، أنت المسؤول قانونياً عن صحة ودقة كل المعلومات التي تنشرها (الموقع، المساحة، السعر، الملكية، المستندات، الصور، بيانات التواصل). ممنوع نشر معلومات مضللة أو غير صحيحة أو منتحلة أو مخالفة للقوانين.</span>
        </div>
        {editId && (
          <div className="mt-3 inline-flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-full px-3 py-1.5">
            <Info className="w-3.5 h-3.5" /> {t.wizard.editWarn}
          </div>
        )}
        <div className="mt-4 flex items-center gap-3">
          <Progress value={progress} className="flex-1" />
          <div className="text-xs font-semibold text-slate-500 shrink-0">{step}/{totalSteps} · {t.wizard.steps[step - 1]}</div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-8 space-y-5">
        {step === 1 && (
          <>
            <h2 className="text-lg font-bold text-emerald-950">{t.wizard.pickType}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {PROPERTY_TYPES.map((c) => (
                <button key={c.key} data-testid={`wizard-type-${c.key}`} onClick={() => set("type", c.key)}
                  className={`p-4 rounded-xl border text-start transition ${d.type === c.key ? "border-emerald-900 bg-emerald-50 ring-2 ring-emerald-900/20" : "border-slate-200 hover:border-emerald-300"}`}>
                  <div className="w-full aspect-video rounded-lg overflow-hidden mb-2">
                    <img src={c.img} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="font-bold text-emerald-950 text-sm">{lang === "ar" ? c.ar : c.en}</div>
                </button>
              ))}
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <h2 className="text-lg font-bold text-emerald-950">{t.wizard.pickLocation}</h2>
            <LocationPicker city={d.city} area={d.area}
              onChange={(loc) => setD((s) => ({ ...s, ...loc, governorate: loc.city || s.governorate }))}
              allowAll={false} creatableArea={true} />
            <div>
              <Label>{t.wizard.pickOnMap}</Label>
              <MapPicker value={d.lat && d.lng ? { lat: d.lat, lng: d.lng } : null}
                onChange={(p) => { set("lat", p.lat); set("lng", p.lng); }}
                center={AMMAN_CENTER} height={320} />
              {d.lat && <div className="text-xs text-slate-500 mt-1 fl-num">{d.lat.toFixed(5)}, {d.lng.toFixed(5)}</div>}
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <h2 className="text-lg font-bold text-emerald-950">{t.wizard.details}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {fields.map((k) => (
                <div key={k} className={k === "notes" ? "sm:col-span-2" : ""}>
                  <Label>{t.fields[k] || k}</Label>
                  {k === "notes" ? (
                    <Textarea rows={3} value={d.details[k] || ""} onChange={(e) => setDetail(k, e.target.value)} />
                  ) : (
                    <Input type={["area_sqm", "land_area", "built_area", "frontage", "streets", "bedrooms", "bathrooms", "floor", "age", "floors", "units", "income"].includes(k) ? "number" : "text"}
                      value={d.details[k] || ""} onChange={(e) => setDetail(k, ["area_sqm", "land_area", "built_area", "frontage", "streets", "bedrooms", "bathrooms", "floor", "age", "floors", "units", "income"].includes(k) ? (e.target.value ? Number(e.target.value) : "") : e.target.value)} />
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {step === 4 && (
          <>
            <h2 className="text-lg font-bold text-emerald-950">{t.wizard.addPhotos}</h2>
            <p className="text-sm text-slate-500">{t.wizard.photosHint}</p>
            <label data-testid="wizard-upload" className="block border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-emerald-900 transition">
              <Upload className="w-6 h-6 mx-auto text-slate-500" />
              <div className="mt-2 text-sm">{lang === "ar" ? "اسحب الصور هنا أو اضغط للاختيار" : "Drop photos here or click to select"}</div>
              <input type="file" accept="image/*" multiple hidden onChange={(e) => onFiles(e.target.files)} />
            </label>
            {d.images.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {d.images.map((im, idx) => (
                  <div key={idx} className={`relative aspect-square rounded-lg overflow-hidden border-2 ${idx === d.cover_index ? "border-amber-500" : "border-slate-200"}`}>
                    <img src={fileUrl(im)} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => setD((s) => ({ ...s, images: s.images.filter((_, i) => i !== idx), cover_index: 0 }))} className="absolute top-1 end-1 w-6 h-6 rounded-full bg-black/60 text-white grid place-items-center"><X className="w-3 h-3" /></button>
                    <button onClick={() => set("cover_index", idx)} className="absolute bottom-1 start-1 text-xs bg-white/90 rounded px-1.5 py-0.5 flex items-center gap-1"><Star className="w-3 h-3" /> {idx === d.cover_index ? t.wizard.cover : t.wizard.makeCover}</button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {step === 5 && (
          <>
            <h2 className="text-lg font-bold text-emerald-950">{t.wizard.priceContact}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>{t.wizard.askedPrice} ({t.common.currency})</Label>
                <Input data-testid="wizard-price" type="number" required value={d.price} onChange={(e) => set("price", e.target.value)} />
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch checked={d.price_negotiable} onCheckedChange={(v) => set("price_negotiable", v)} />
                <span className="text-sm">{t.wizard.negotiable}</span>
              </div>
              <div><Label>{t.wizard.phoneNum}</Label><Input value={d.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} /></div>
              <div><Label>{t.wizard.waNum}</Label><Input value={d.contact_whatsapp} onChange={(e) => set("contact_whatsapp", e.target.value)} /></div>
              <div className="flex items-center gap-3"><Switch checked={d.show_phone} onCheckedChange={(v) => set("show_phone", v)} /><span className="text-sm">{t.wizard.showPhone}</span></div>
              <div className="flex items-center gap-3"><Switch checked={d.show_whatsapp} onCheckedChange={(v) => set("show_whatsapp", v)} /><span className="text-sm">{t.wizard.showWA}</span></div>
            </div>
          </>
        )}

        {step === 6 && (
          <>
            <h2 className="text-lg font-bold text-emerald-950">{t.wizard.writeDesc}</h2>
            <div><Label>{t.wizard.title}</Label><Input data-testid="wizard-title" value={d.title} onChange={(e) => set("title", e.target.value)} placeholder={t.wizard.titlePh} /></div>
            <div>
              <Label>{t.wizard.writeDesc}</Label>
              <Textarea data-testid="wizard-desc" rows={7} value={d.description} onChange={(e) => set("description", e.target.value)} />
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <Button data-testid="wizard-ai" onClick={aiEnhance} disabled={aiLoading} variant="outline" className="gap-2"><Sparkles className="w-4 h-4 text-amber-500" /> {aiLoading ? "…" : t.wizard.aiBtn}</Button>
                <span className="text-xs text-slate-500">{t.wizard.aiHint}</span>
              </div>
            </div>
          </>
        )}

        {step === 7 && (
          <>
            <h2 className="text-lg font-bold text-emerald-950">{t.wizard.preview}</h2>
            <div className="bg-slate-50 rounded-xl p-4 space-y-2">
              <div className="text-xs text-slate-500">{typeLabel(d.type, lang)}{d.city ? ` · ${d.city}` : ""}{d.area ? ` · ${d.area}` : ""}</div>
              <div className="text-lg font-bold text-emerald-950">{d.title || "—"}</div>
              <div className="fl-num text-2xl font-extrabold text-emerald-950">{formatPrice(d.price)} <span className="text-sm text-slate-500 font-normal">{t.common.currency}</span></div>
              {d.images.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-2">{d.images.slice(0, 6).map((im, i) => <img key={i} src={fileUrl(im)} alt="" className="aspect-square object-cover rounded-lg" />)}</div>
              )}
              {d.description && <p className="text-sm text-slate-700 whitespace-pre-wrap">{d.description}</p>}
            </div>
            <label className="flex items-start gap-3 text-sm text-slate-700 bg-emerald-50 border border-emerald-200 rounded-xl p-3 cursor-pointer">
              <input data-testid="wizard-terms" type="checkbox" checked={agreedTerms} onChange={(e) => setAgreedTerms(e.target.checked)} className="mt-1 w-4 h-4 accent-emerald-900" />
              <span>أُقرّ بأن جميع المعلومات المدخلة صحيحة وأني صاحب/مفوّض بالتصرف في هذا العقار، وأوافق على{" "}
                <a href="/terms" target="_blank" rel="noreferrer" className="text-emerald-900 font-semibold hover:text-amber-700">شروط الاستخدام</a>{" "}و
                <a href="/disclaimer" target="_blank" rel="noreferrer" className="text-emerald-900 font-semibold hover:text-amber-700">إخلاء المسؤولية</a>{" "}الخاصة بفلوريت.
              </span>
            </label>
          </>
        )}

        <div className="flex items-center justify-between pt-4 border-t border-slate-200">
          <Button variant="ghost" disabled={step === 1} onClick={() => setStep(step - 1)} className="gap-1" data-testid="wizard-back">
            <ChevronRight className={`w-4 h-4 ${lang === "en" ? "rotate-180" : ""}`} /> {t.wizard.back}
          </Button>
          {step < totalSteps ? (
            <Button data-testid="wizard-next" onClick={() => canNext() ? setStep(step + 1) : toast.error("أكمل الحقول المطلوبة")} className="bg-emerald-900 hover:bg-emerald-950 gap-1">
              {t.wizard.next} <ChevronLeft className={`w-4 h-4 ${lang === "en" ? "rotate-180" : ""}`} />
            </Button>
          ) : (
            <Button data-testid="wizard-publish" onClick={submit} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-emerald-950 font-bold">
              {saving ? "…" : t.wizard.publish}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
